import React from 'react';
import { parseSearchLogs, formatLogSummary } from '../utils/logParser';

interface SearchLogsSummaryProps {
  logs: string[];
  showRawLogs?: boolean;
  creatorFilterStats?: any; // For Full Hashtag Search
  searchType?: string;
  keyword?: string; // Added to show actual keyword
}

const SearchLogsSummary: React.FC<SearchLogsSummaryProps> = ({
  logs,
  showRawLogs = false,
  creatorFilterStats,
  searchType,
  keyword = ''
}) => {
  if (!logs || logs.length === 0) {
    return (
      <div style={{
        padding: '12px',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#666',
        textAlign: 'center'
      }}>
        No search logs available
      </div>
    );
  }

  // If logs are already formatted (e.g., from backend for full-hashtag), use them directly
  let formattedLines: string[] = [];

  // Check if logs are already properly formatted (contains emoji indicators)
  const hasFormattedLogs = logs.some(log =>
    log.includes('📊') || log.includes('📌') || log.includes('📧') ||
    log.includes('✅') || log.includes('⚠️') || log.includes('────')
  );

  if (hasFormattedLogs) {
    // Use logs directly as they're already formatted
    formattedLines = logs;
  } else if (searchType === 'full-hashtag' && creatorFilterStats) {
    // Legacy: Format Full Hashtag Search stats directly (kept for backward compatibility)
    formattedLines = [
      `📊 Search Summary for "${keyword}"`,
      '────────────────────────────────',
      `Initial posts found: ${creatorFilterStats.originalCount || 0}`,
      '',
      '📌 Filtering Pipeline:',
    ];

    if (creatorFilterStats.withEmails !== undefined) {
      formattedLines.push('');
      formattedLines.push('📧 Email Status:');
      formattedLines.push(`├─ With email: ${creatorFilterStats.withEmails} creators`);
      formattedLines.push(`└─ Without email: ${creatorFilterStats.withoutEmails || 0} creators`);
    }

    if (creatorFilterStats.excludedExisting?.length > 0) {
      formattedLines.push('');
      formattedLines.push(`🔄 Excluded existing: ${creatorFilterStats.excludedExisting.length} creators`);
    }

    formattedLines.push('');
    const withEmail = creatorFilterStats.withEmails || 0;
    const withoutEmail = creatorFilterStats.withoutEmails || 0;
    const excluded = creatorFilterStats.excludedExisting?.length || 0;
    const totalCreators = withEmail + withoutEmail;

    // Calculate actual new creators that can be added
    const newCreatorsToAdd = Math.max(0, withEmail - excluded);

    if (withEmail > 0) {
      if (excluded > 0) {
        formattedLines.push(`✅ Result: ${newCreatorsToAdd} new creators to add (${withEmail} with email, ${excluded} already in DB)`);
      } else {
        formattedLines.push(`✅ Result: ${withEmail} new creators to add (all have emails)`);
      }
    } else {
      formattedLines.push(`⚠️ Result: No creators with email found (${totalCreators} total creators)`);
    }
  } else {
    // Use existing log parser for other search types
    const summary = parseSearchLogs(logs);
    formattedLines = formatLogSummary(summary);
  }

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      borderRadius: '8px',
      padding: '16px',
      fontFamily: 'monospace',
      fontSize: '12px'
    }}>
      {/* Formatted Summary */}
      <div style={{
        backgroundColor: '#0d1117',
        border: '1px solid #30363d',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: showRawLogs ? '12px' : '0'
      }}>
        {formattedLines.map((line, index) => {
          let style: React.CSSProperties = {
            marginBottom: '4px',
            whiteSpace: 'pre',
            color: '#c9d1d9'
          };

          // Apply different colors based on content
          if (line.startsWith('📊')) {
            style.color = '#58a6ff';
            style.fontWeight = 'bold';
            style.fontSize = '14px';
          } else if (line.startsWith('─')) {
            style.color = '#30363d';
          } else if (line.startsWith('📌') || line.startsWith('📧')) {
            style.color = '#8b949e';
            style.fontWeight = 'bold';
            style.marginTop = '8px';
          } else if (line.startsWith('✅')) {
            style.color = '#3fb950';
            style.fontWeight = 'bold';
            style.marginTop = '8px';
          } else if (line.startsWith('⚠️')) {
            style.color = '#d29922';
            style.fontWeight = 'bold';
            style.marginTop = '8px';
          } else if (line.includes('→')) {
            // Filter stages
            if (line.includes('(-')) {
              // Has filtered count
              const parts = line.split('(-');
              return (
                <div key={index} style={style}>
                  <span>{parts[0]}</span>
                  <span style={{ color: '#f85149' }}>(-{parts[1]}</span>
                </div>
              );
            }
          }

          return (
            <div key={index} style={style}>
              {line}
            </div>
          );
        })}
      </div>

      {/* Raw Logs (Optional) */}
      {showRawLogs && (
        <details>
          <summary style={{
            cursor: 'pointer',
            color: '#8b949e',
            fontSize: '11px',
            marginBottom: '8px',
            userSelect: 'none'
          }}>
            Show raw logs
          </summary>
          <div style={{
            backgroundColor: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: '6px',
            padding: '8px',
            maxHeight: '200px',
            overflowY: 'auto',
            fontSize: '11px',
            color: '#8b949e'
          }}>
            {logs.map((log, index) => (
              <div key={index} style={{ marginBottom: '2px' }}>
                {log}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default SearchLogsSummary;